import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, compressImage, pickImage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLoading } from "@/lib/loading";
import { useToast } from "@/lib/toast";
import { maskE164 } from "@/lib/phone";
import { Avatar, Button, Card, Input, Overline, Toggle } from "@/components/app/Primitives";
import { PhoneInput } from "@/components/app/PhoneInput";
import { StackPage } from "@/components/app/Layout";

export default function ProfileEdit() {
  const { user, refreshMe } = useAuth();
  const toast = useToast();
  const { runWithLoading } = useLoading();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.full_name || "");
  const [designation, setDesignation] = useState(user?.designation || "");
  const [company, setCompany] = useState(user?.company_name || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [editingMobile, setEditingMobile] = useState(false);
  const [newMobile, setNewMobile] = useState("");
  const [changingMobile, setChangingMobile] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [requested, setRequested] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [changingEmail, setChangingEmail] = useState(false);
  const [hideLastSeen, setHideLastSeen] = useState(!!user?.hide_last_seen);
  const [savingPrivacy, setSavingPrivacy] = useState(false);

  const err = (e, f) => toast.show(e instanceof Error ? e.message : f, "error");
  const nameDirty = name.trim() !== (user?.full_name || "").trim();
  const desigDirty = designation.trim() !== (user?.designation || "").trim();
  const companyDirty = company.trim() !== (user?.company_name || "").trim();
  const dirty = nameDirty || desigDirty || companyDirty;

  const toggleHide = async (next) => {
    setSavingPrivacy(true);
    try { await api("/auth/profile", { method: "PATCH", body: { hide_last_seen: next } }); setHideLastSeen(next); await refreshMe(); toast.show(next ? "Your last seen is now hidden" : "Your last seen is now visible", "success"); }
    catch (e) { err(e, "Failed to update privacy"); } finally { setSavingPrivacy(false); }
  };
  const saveProfile = async () => {
    if (!dirty) return;
    setSavingProfile(true);
    try {
      await api("/auth/profile", { method: "PATCH", body: { full_name: nameDirty ? name.trim() : undefined, designation: desigDirty ? designation.trim() : undefined, company_name: companyDirty ? company.trim() : undefined } });
      await refreshMe(); toast.show("Profile saved", "success");
    } catch (e) { err(e, "Save failed"); } finally { setSavingProfile(false); }
  };
  const savePhoto = async () => {
    const file = await pickImage();
    if (!file) return;
    setSavingPhoto(true);
    try { const r = await compressImage(file, 512, 0.4, true); await api("/auth/profile", { method: "PATCH", body: { avatar_base64: r.dataUrl } }); await refreshMe(); toast.show("Photo updated", "success"); }
    catch (e) { err(e, "Save failed"); } finally { setSavingPhoto(false); }
  };
  const removePhoto = async () => {
    setSavingPhoto(true);
    try { await api("/auth/profile", { method: "PATCH", body: { avatar_base64: "" } }); await refreshMe(); toast.show("Photo removed", "info"); }
    catch (e) { err(e, "Remove failed"); } finally { setSavingPhoto(false); }
  };
  const saveMobile = async () => {
    if (!newMobile) return toast.show("Enter a valid mobile number", "error");
    setChangingMobile(true);
    try {
      const r = await runWithLoading(() => api("/auth/change-mobile", { method: "POST", body: { mobile_number: newMobile } }), "Sending OTP…");
      await refreshMe(); setEditingMobile(false); setNewMobile("");
      if (r?.sms_sent === false) toast.show(`Number saved, but OTP could not be delivered${r.sms_error ? ` (${r.sms_error})` : ""}. Please try Resend on the verify screen.`, "error");
      else toast.show("OTP sent to your new mobile. Enter it to verify.", "success");
      navigate("/verify?target=mobile");
    } catch (e) { err(e, "Failed to update mobile"); } finally { setChangingMobile(false); }
  };
  const requestEmailChange = async () => {
    if (!newEmail.trim() || !newEmail.includes("@")) return toast.show("Enter a valid email", "error");
    if (!currentPw) return toast.show("Enter your current password to confirm", "error");
    setChangingEmail(true);
    try { await api("/auth/change-email", { method: "POST", body: { email: newEmail.trim().toLowerCase(), password: currentPw } }); toast.show("OTP sent to the new address. Enter the code below.", "info"); setRequested(true); }
    catch (e) { err(e, "Failed"); } finally { setChangingEmail(false); }
  };
  const confirmEmailChange = async () => {
    if (!emailCode.trim()) return toast.show("Enter the 6-digit code", "error");
    setChangingEmail(true);
    try {
      await api("/auth/confirm-email-change", { method: "POST", body: { code: emailCode.trim() } });
      await refreshMe(); toast.show("Email updated", "success");
      setEditingEmail(false); setNewEmail(""); setCurrentPw(""); setEmailCode(""); setRequested(false);
    } catch (e) { err(e, "Verification failed"); } finally { setChangingEmail(false); }
  };

  if (!user) return null;
  const link = "text-[13px] font-bold text-a-primary hover:underline";
  return (
    <StackPage title="Edit profile" subtitle="Changes save immediately." testID="profile-edit-page">
      <Card>
        <div className="flex items-center gap-3.5">
          <Avatar src={user.avatar_base64} name={user.full_name} size={72} />
          <div className="flex-1"><p className="font-extrabold text-a-text">Profile photo</p><p className="text-xs text-a-muted">PNG or JPG · square.</p></div>
        </div>
        <div className="mt-3 flex gap-2">
          <Button testID="edit-photo-change" label={user.avatar_base64 ? "Change" : "Add photo"} onPress={savePhoto} loading={savingPhoto} fullWidth={false} variant="secondary" />
          {user.avatar_base64 ? <Button testID="edit-photo-remove" label="Remove" onPress={removePhoto} loading={savingPhoto} fullWidth={false} variant="ghost" /> : null}
        </div>
      </Card>
      <Card className="space-y-3">
        <Input testID="edit-name" label="Name *" placeholder="Full name" value={name} onChangeText={setName} />
        <Input testID="edit-designation" label="Designation" placeholder="e.g. Product Manager" value={designation} onChangeText={setDesignation} />
        <Input testID="edit-company" label="Company" placeholder="e.g. Acme Corp" value={company} onChangeText={setCompany} />
        <Button testID="edit-save" label="Save changes" onPress={saveProfile} loading={savingProfile} disabled={!dirty} />
      </Card>
      <Card>
        <div className="flex items-center">
          <div className="flex-1"><p className="font-extrabold text-a-text">Mobile</p><p className="text-xs text-a-muted">{user.mobile_number ? `${maskE164(user.mobile_number)} · ${user.mobile_verified ? "Verified" : "Not verified"}` : "Not set"}</p></div>
          <button onClick={() => setEditingMobile((v) => !v)} className={link} data-testid="edit-mobile-toggle">{editingMobile ? "Cancel" : user.mobile_number ? "Change" : "Add"}</button>
        </div>
        {editingMobile ? (
          <div className="mt-3 space-y-2.5">
            <PhoneInput testID="edit-new-mobile" label="New mobile" placeholder="98765 43210" valueE164={newMobile} onChangeE164={(e) => setNewMobile(e)} />
            <Button testID="edit-mobile-save" label="Save & send OTP" onPress={saveMobile} loading={changingMobile} />
          </div>
        ) : null}
      </Card>
      <Card>
        <div className="flex items-center">
          <div className="min-w-0 flex-1"><p className="font-extrabold text-a-text">Email</p><p className="truncate text-xs text-a-muted">{user.email} · {user.email_verified ? "Verified" : "Not verified"}</p></div>
          <button onClick={() => { setEditingEmail((v) => !v); setRequested(false); }} className={link} data-testid="edit-email-toggle">{editingEmail ? "Cancel" : "Change"}</button>
        </div>
        {editingEmail ? (
          <div className="mt-3 space-y-2.5">
            {!requested ? (
              <>
                <Input testID="edit-new-email" label="New email" type="email" placeholder="you@company.com" value={newEmail} onChangeText={setNewEmail} />
                <Input testID="edit-current-password" label="Current password" type="password" placeholder="Confirm with your password" value={currentPw} onChangeText={setCurrentPw} />
                <Button testID="edit-email-send" label="Send verification code" onPress={requestEmailChange} loading={changingEmail} />
              </>
            ) : (
              <>
                <p className="text-[13px] text-a-muted">Enter the 6-digit code sent to <b className="text-a-text">{newEmail}</b>.</p>
                <Input testID="edit-email-code" label="Verification code" inputMode="numeric" maxLength={6} value={emailCode} onChangeText={(t) => setEmailCode(t.replace(/[^0-9]/g, ""))} />
                <Button testID="edit-email-confirm" label="Confirm & update email" onPress={confirmEmailChange} loading={changingEmail} />
                <button onClick={requestEmailChange} disabled={changingEmail} className="w-full text-center text-[13px] font-bold text-a-primary">Resend code</button>
              </>
            )}
          </div>
        ) : null}
      </Card>
      <Card>
        <Overline className="mb-2">Privacy</Overline>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="font-bold text-a-text">Hide my last seen</p>
            <p className="mt-0.5 text-xs text-a-muted">Others won&apos;t see when you were last active. You&apos;ll still appear as &quot;Active now&quot; in real time while the app is open.</p>
          </div>
          <Toggle testID="edit-hide-last-seen" value={hideLastSeen} disabled={savingPrivacy} onChange={toggleHide} />
        </div>
      </Card>
    </StackPage>
  );
}
